import React, { useState } from 'react';
import axios from 'axios';

import CardDialog from '~/components/CardDialog';
import LoadingSwitch from '~/components/core/Loading/LoadingSwitch';
import type { ConfigurableOptionProps } from '~/components/core/Molecules/ConfigurableOption';
import ConfigurableOption from '~/components/core/Molecules/ConfigurableOption';
import useNotesConfiguration from '~/components/hooks/useNotesConfiguration';
import NoteEditTimeDialog from '~/components/SystemConfiguration/NotesConfiguration/NoteEditTimeDialog';
import NotesPerPageEditDialog from '~/components/SystemConfiguration/NotesConfiguration/NotesPerPageEditDialog';
import type {
  NotesConfiguration,
  NotesConfigurationUpdateParams,
} from '~/components/SystemConfiguration/NotesConfiguration/types';
import { reportAxiosError } from '~/Utils';

import Heading from '../../core/TextComponents/Heading';

const NotesConfigurationScreen: React.FC = () => {
  const { configuration, isLoading, isError, reloadData, notesConfigurationSysconfigRoute } =
    useNotesConfiguration(true);

  const [isUpdatingConfiguration, setIsUpdatingConfiguration] = useState(false);

  const handleUpdateConfiguration = async (values: NotesConfigurationUpdateParams, throwOnError = true) => {
    try {
      setIsUpdatingConfiguration(true);
      await axios.patch(notesConfigurationSysconfigRoute, values);

      await reloadData();
    } catch (error) {
      await reportAxiosError(error);
      if (throwOnError) {
        throw error;
      }
    } finally {
      setIsUpdatingConfiguration(false);
    }
  };

  const configurationOptions: ConfigurableOptionProps<NotesConfiguration>[] = [
    {
      isEnabled: configuration?.is_edit_note_enabled,
      onChangeToggle: async (isEnabled: boolean) =>
        await handleUpdateConfiguration({ is_edit_note_enabled: isEnabled }, false),
      configurationView: <Heading variant={Heading.TYPES.H3}>Allow note editing</Heading>,
      EditConfigurationDialogComponent: NoteEditTimeDialog,
      configuration,
      configurationKey: 'note_edit_time_in_seconds',
      onSubmitDialog: handleUpdateConfiguration,
    },
    {
      isEnabled: configuration?.is_notes_expanded_by_default,
      onChangeToggle: async (isEnabled: boolean) =>
        await handleUpdateConfiguration({ is_notes_expanded_by_default: isEnabled }, false),
      configurationView: <Heading variant={Heading.TYPES.H3}>Notes are expanded by default</Heading>,
      configuration,
    },
    {
      isEnabled: configuration?.is_default_notes_per_page_enabled,
      onChangeToggle: async (isEnabled: boolean) =>
        await handleUpdateConfiguration({ is_default_notes_per_page_enabled: isEnabled }, false),
      configurationView: <Heading variant={Heading.TYPES.H3}>Set default notes per page</Heading>,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      EditConfigurationDialogComponent: NotesPerPageEditDialog,
      configuration,
      configurationKey: 'default_notes_per_page',
      onSubmitDialog: handleUpdateConfiguration,
    },
    {
      isEnabled: !!configuration?.is_rich_text_notes_enabled,
      onChangeToggle: async (isEnabled: boolean) =>
        await handleUpdateConfiguration({ is_rich_text_notes_enabled: isEnabled }, false),
      configurationView: <Heading variant={Heading.TYPES.H3}>Enable rich text notes</Heading>,
      configuration,
    },
  ];

  return (
    <div className="bg-slate-100 pt-32">
      <CardDialog width="md" noCardTitle>
        <LoadingSwitch isError={isError} isLoading={isLoading}>
          {configurationOptions.map(
            (
              {
                isEnabled,
                onChangeToggle,
                configurationView,
                configuration,
                EditConfigurationDialogComponent,
                onSubmitDialog,
                configurationKey,
              },
              idx
            ) => (
              <ConfigurableOption<NotesConfiguration>
                key={idx}
                isEnabled={isEnabled}
                onChangeToggle={onChangeToggle}
                configurationView={configurationView}
                configuration={configuration}
                EditConfigurationDialogComponent={EditConfigurationDialogComponent}
                showOnly={isUpdatingConfiguration}
                onSubmitDialog={onSubmitDialog}
                configurationKey={configurationKey}
              />
            )
          )}
        </LoadingSwitch>
      </CardDialog>
    </div>
  );
};

export default NotesConfigurationScreen;
